import React, { useEffect, useRef, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ServicePageHeader } from "@/components/ServiceHistoryButton";
import { SimammaBadge } from "@/components/Logo";
import { toast } from "sonner";
import { Upload, Check, ShieldCheck, QrCode, Search, Info } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SellEsim() {
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [meta, setMeta] = useState({ source: "mock", mode: "sandbox" });
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [form, setForm] = useState({ customer_id: "", package_id: "", customer_email: "", doc_type: "carta_identita", doc_number: "" });
  const [kyc, setKyc] = useState({ id_document_path: "", id_document_name: "", selfie_path: "", selfie_name: "" });
  const [uploading, setUploading] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptNoRefund, setAcceptNoRefund] = useState(false);
  const [result, setResult] = useState(null);
  const [kycEnabled, setKycEnabled] = useState(false);
  const [kycReq, setKycReq] = useState(null);
  const [kycBusy, setKycBusy] = useState(false);
  const docRef = useRef();
  const selfieRef = useRef();

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/esim/packages").then((r) => { setPackages(r.data.packages || []); setMeta({ source: r.data.source, mode: r.data.mode }); }).catch(() => {});
    api.get("/identity/config").then((r) => setKycEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  const selectedPkg = packages.find((p) => p.package_id === form.package_id);

  const countries = React.useMemo(() => {
    const set = new Set(packages.map((p) => p.country).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [packages]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return packages.filter((p) => {
      if (countryFilter !== "all" && p.country !== countryFilter) return false;
      if (q && !`${p.title || ""} ${p.country || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [packages, search, countryFilter]);

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", kind === "doc" ? "kyc_id_document" : "kyc_selfie");
    try {
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "doc") setKyc((k) => ({ ...k, id_document_path: data.storage_path, id_document_name: file.name }));
      else setKyc((k) => ({ ...k, selfie_path: data.storage_path, selfie_name: file.name }));
      toast.success(kind === "doc" ? "Documento caricato" : "Selfie caricato");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento"); }
    finally { setUploading(""); }
  };

  const requestKyc = async () => {
    if (!form.customer_id) return toast.error("Seleziona prima un cliente");
    setKycBusy(true);
    try {
      const { data } = await api.post("/esim/kyc-request", { customer_id: form.customer_id });
      setKycReq(data);
      toast.success(data.email_sent ? "Richiesta inviata via email al cliente" : "Link di identificazione creato");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore richiesta identificazione"); }
    finally { setKycBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Seleziona un cliente");
    if (!form.package_id) return toast.error("Seleziona un pacchetto eSIM");
    if (!kycEnabled && (!kyc.id_document_path || !kyc.selfie_path)) return toast.error("Carica documento e selfie del cliente");
    setLoading(true);
    try {
      let data;
      if (kycEnabled) {
        ({ data } = await api.post("/esim/sell-quick", { customer_id: form.customer_id, package_id: form.package_id }));
      } else {
        ({ data } = await api.post("/esim/sell", {
          customer_id: form.customer_id, package_id: form.package_id,
          customer_email: form.customer_email || null,
          id_document_path: kyc.id_document_path, selfie_path: kyc.selfie_path,
          doc_type: form.doc_type, doc_number: form.doc_number || null,
        }));
      }
      setResult(data);
      toast.success(`eSIM venduta — addebitati ${euro(data.amount)}`);
      setForm({ customer_id: "", package_id: "", customer_email: "", doc_type: "carta_identita", doc_number: "" });
      setKyc({ id_document_path: "", id_document_name: "", selfie_path: "", selfie_name: "" });
      setKycReq(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore vendita eSIM"); }
    finally { setLoading(false); }
  };

  return (
    <div className="service-page-wide space-y-5 w-full max-w-none" data-testid="sell-esim-page">
      <ServicePageHeader service="esim">
        <div className="flex items-center gap-2"><SimammaBadge /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">eSIM</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2">Vendi eSIM</h1>
        <p className="text-sm text-muted-foreground mt-1">Riconosci il cliente, scegli il pacchetto, paga col wallet: il QR viene inviato via email.</p>
        <div className="flex gap-2 mt-2">
          <Badge variant="outline" className="uppercase">{meta.mode}</Badge>
          <Badge variant="outline">{meta.source === "airalo" ? "Catalogo eSIM" : "Catalogo demo"}</Badge>
        </div>
      </ServicePageHeader>

      {result && (
        <Card className="p-6 glow-border" data-testid="esim-result-card" style={{ background: "#0f1b2d" }}>
          <div className="flex items-center gap-2 text-teal-400"><Check className="w-5 h-5" /><span className="font-semibold">eSIM {result.package_title} pronta</span></div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <img src={`${BACKEND}${result.qr_url}`} alt="QR eSIM" width={180} height={180} className="rounded-lg bg-white p-2" data-testid="esim-qr-image" />
            <div className="text-sm text-white/80 space-y-1 break-all">
              <p><span className="text-white/50">ICCID:</span> <span className="font-mono">{result.iccid}</span></p>
              <p><span className="text-white/50">LPA:</span> <span className="font-mono">{result.lpa}</span></p>
              <p><span className="text-white/50">Email cliente:</span> {result.email_sent ? "inviata ✅" : "non inviata"}</p>
              {result.mock && <p className="text-amber-400 text-xs">Modalità demo: eSIM simulata (non attivabile).</p>}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 glow-border">
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label>Cliente</Label>
            <Select value={form.customer_id} onValueChange={(v) => { const c = customers.find((x) => x.id === v); setForm({ ...form, customer_id: v, customer_email: c?.email || "" }); }}>
              <SelectTrigger className="mt-1.5" data-testid="esim-customer-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone && <> — <PhoneDisplay value={c.phone} data-testid={`esim-customer-phone-${c.id}`} /></>}</SelectItem>)}</SelectContent>
            </Select>
            {customers.length === 0 && <p className="text-xs text-amber-500 mt-1">Aggiungi prima un cliente nella sezione Clienti.</p>}
          </div>

          <div>
            <Label>Pacchetto eSIM</Label>
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 mt-1.5">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca paese o pacchetto…" data-testid="esim-search-input" />
              </div>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="sm:w-52" data-testid="esim-country-select"><SelectValue placeholder="Tutti i paesi" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutti i paesi/regioni</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
              <SelectTrigger className="mt-2" data-testid="esim-package-select"><SelectValue placeholder={`Seleziona pacchetto (${filtered.length} disponibili)`} /></SelectTrigger>
              <SelectContent className="max-h-72">{filtered.slice(0, 300).map((p) => <SelectItem key={p.package_id} value={p.package_id}>{p.title} — {euro(p.sale_price)}</SelectItem>)}</SelectContent>
            </Select>
            {filtered.length === 0 && <p className="text-xs text-amber-500 mt-1">Nessun pacchetto per questo filtro.</p>}
            {selectedPkg && <p className="text-xs text-muted-foreground mt-1">{selectedPkg.country} • {selectedPkg.data} • {selectedPkg.validity} • Prezzo: <span className="text-foreground font-semibold">{euro(selectedPkg.sale_price)}</span></p>}
          </div>

          {kycEnabled ? (
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="esim-kyc-selfie-block">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Identificazione cliente
                  <span title="Il cliente riceve un link e fa documento + selfie dal suo telefono. Puoi vendere subito: la verifica arriva in coda admin." data-testid="esim-kyc-info"><Info className="w-3.5 h-3.5 text-muted-foreground" /></span>
                </span>
                <Button next type="button" size="sm" variant="outline" className="gap-2" disabled={kycBusy || !form.customer_id} onClick={requestKyc} data-testid="esim-kyc-request-button">
                  <ShieldCheck className="w-4 h-4" /> {kycBusy ? "Invio…" : "Invia link al cliente"}
                </Button>
              </div>
              {kycReq && (
                <div className="rounded-lg border border-border p-3 text-xs space-y-2" data-testid="esim-kyc-link-box">
                  <p className="text-teal-500">{kycReq.email_sent ? "Inviata via email al cliente." : "Link creato."} Il cliente completa dal telefono:</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={kycReq.handoff_url} className="flex-1 bg-secondary rounded px-2 py-1 text-xs" data-testid="esim-kyc-link" />
                    <Button type="button" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(kycReq.handoff_url); toast.success("Link copiato"); }} data-testid="esim-kyc-copy">Copia</Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /><span className="font-semibold text-sm">Riconoscimento cliente (obbligatorio)</span></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo documento</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="esim-doctype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carta_identita">Carta d'identità</SelectItem>
                    <SelectItem value="passaporto">Passaporto</SelectItem>
                    <SelectItem value="patente">Patente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Numero documento</Label><Input className="mt-1" data-testid="esim-docnumber-input" value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} placeholder="Es. CA1234567" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <input ref={docRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload("doc", e.target.files?.[0])} data-testid="esim-doc-upload" />
                <Button type="button" variant={kyc.id_document_path ? "secondary" : "outline"} className="w-full gap-2" disabled={uploading === "doc"} onClick={() => docRef.current?.click()} data-testid="esim-doc-button">
                  {kyc.id_document_path ? <><Check className="w-4 h-4" /> Documento ok</> : <><Upload className="w-4 h-4" /> {uploading === "doc" ? "Caricamento…" : "Carica documento"}</>}
                </Button>
                {kyc.id_document_name && <p className="text-[11px] text-muted-foreground mt-1 truncate">{kyc.id_document_name}</p>}
              </div>
              <div>
                <input ref={selfieRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => upload("selfie", e.target.files?.[0])} data-testid="esim-selfie-upload" />
                <Button type="button" variant={kyc.selfie_path ? "secondary" : "outline"} className="w-full gap-2" disabled={uploading === "selfie"} onClick={() => selfieRef.current?.click()} data-testid="esim-selfie-button">
                  {kyc.selfie_path ? <><Check className="w-4 h-4" /> Selfie ok</> : <><Upload className="w-4 h-4" /> {uploading === "selfie" ? "Caricamento…" : "Carica selfie"}</>}
                </Button>
                {kyc.selfie_name && <p className="text-[11px] text-muted-foreground mt-1 truncate">{kyc.selfie_name}</p>}
              </div>
            </div>
          </div>
          )}

          <div><Label>Email cliente (per invio QR)</Label><Input type="email" className="mt-1.5" data-testid="esim-email-input" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="cliente@email.it" /></div>

          <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-border bg-secondary/40 px-3 py-2" data-testid="esim-norefund-check-label">
            <input type="checkbox" checked={acceptNoRefund} onChange={(e) => setAcceptNoRefund(e.target.checked)} data-testid="esim-norefund-check" />
            <span>Il cliente sa che la eSIM <strong>non è rimborsabile</strong></span>
            <span className="ml-auto" title="Appena creato il QR la eSIM è attiva: la vendita è definitiva. Verifica paese, dati e validità prima di confermare." data-testid="esim-norefund-notice"><Info className="w-4 h-4 text-muted-foreground" /></span>
          </label>

          <Button type="submit" disabled={loading || !acceptNoRefund} className="gap-2 w-full basis-full" data-testid="esim-submit-button">
            <QrCode className="w-4 h-4" /> {loading ? "Emissione eSIM…" : `Vendi eSIM${selectedPkg ? ` — ${euro(selectedPkg.sale_price)}` : ""}`}
          </Button>
        </form>
      </Card>
    </div>
  );
}
